import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { PackageCode } from 'api/queries/packageTour';
import { SolidButton } from 'components/Button';
import FieldTrigger from './FieldTrigger';

interface IRoomTypeDropdownProps {
  selected: string[];
  options: PackageCode[];
  onChange: (v: string[]) => void;
}

const RoomTypeDropdown: React.FC<IRoomTypeDropdownProps> = ({
  selected,
  options,
  onChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const label =
    selected.length === 0
      ? t('Any')
      : selected.length === 1
        ? options.find(o => o.code === selected[0])?.name || t('Any')
        : t('{{count}} selected', { count: selected.length });

  const toggle = (v: string) => {
    onChange(
      selected.includes(v) ? selected.filter(r => r !== v) : [...selected, v],
    );
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="flex flex-col gap-1 flex-1 min-w-0 relative">
      <FieldTrigger
        label={t('Room type')}
        showLabel
        open={open}
        filled={selected.length > 0}
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 min-w-[220px] bg-white rounded-xl shadow-lg border border-tt-gray-purple max-h-72 overflow-y-auto">
          <div className="py-1">
            {options.map(r => (
              <label
                key={r.code}
                className="flex items-center gap-2 px-4 py-2.5 hover:bg-tt-verylight-purple cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(r.code)}
                  onChange={() => toggle(r.code)}
                  className="accent-tt-yellow h-4 w-4 rounded"
                />
                {r.name}
              </label>
            ))}
          </div>
          <div className="p-2 border-t border-tt-gray-purple">
            <SolidButton
              onClick={() => setOpen(false)}
              className="w-full !py-2.5"
            >
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomTypeDropdown;
